/* eslint-disable no-undef */
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Header from "../../components/Header/Header";
import { formatarMoeda, formatarMoedaDolar } from "../../utils/formatMoeda";
import "./styles.css";
import { Col, FormGroup, Input, Label, Row, Table, Pagination, PaginationItem, PaginationLink } from "reactstrap";
import api from "../../services/axiosConfig";
import { Context } from "../../hooks/useAuthContext";
import axios from "axios";
import { DateRange } from "react-date-range";
import { FaRegCalendarAlt } from "react-icons/fa";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import FullScreenLoader from "../../components/FullScreenLoader/FullScreenLoader";
import { useNavigate } from "react-router-dom";

function ReceitasUsuarios() {
  const getPrimeiroDiaDoMes = () => {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  };

  const getUltimoDiaDoMes = () => {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
  };

  const navigate = useNavigate();
  const { userData, exibirLogs, isEmailRodrigo } = useContext(Context);

  const [selectedStartDate, setSelectedStartDate] = useState(getPrimeiroDiaDoMes());
  const [selectedEndDate, setSelectedEndDate] = useState(getUltimoDiaDoMes());
  const [apiResponse, setApiResponse] = useState(null);
  const [totalConverted, setTotalConverted] = useState(0);
  const [allUsers, setAllUsers] = useState([]);
  const [mostrarCalendario, setMostrarCalendario] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [tipoContaSelecionado, setTipoContaSelecionado] = useState("");
  const [usuariosFiltrados, setUsuariosFiltrados] = useState([]);
  const [periodTable, setPeriodTable] = useState([
    { startDate: getPrimeiroDiaDoMes(), endDate: getUltimoDiaDoMes(), key: "selection", color: "#3d91ff" },
  ]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  const tiposDeContaUnicos = useMemo(() => {
    if (!allUsers.length) return [];
    const tipos = allUsers.map((user) => user.tipoConta).filter((tipo) => tipo === "usuario" || tipo === "influenciador");
    return [...new Set(tipos)];
  }, [allUsers]);

  const tableData = useMemo(() => apiResponse?.data || [], [apiResponse]);
  const totalReceitaTable = useMemo(() => apiResponse?.totalReceita || 0, [apiResponse]);
  const totalPages = useMemo(() => apiResponse?.totalPages || 0, [apiResponse]);

  const getDolarCotation = useCallback(async (receita) => {
    const tokenDolarAPI = import.meta.env.VITE_TOKEN_DOLAR_API;
    const url = `https://economia.awesomeapi.com.br/json/last/USD-BRL?token=${tokenDolarAPI}`;
    try {
      const { data } = await axios.get(url);
      const cotation = data?.USDBRL.bid;
      return formatarMoeda(Number(receita * cotation)) || "API ERROR";
    } catch (error) {
      exibirLogs && console.error("Erro ao buscar cotação do dólar: ", error);
      return formatarMoeda(0.0);
    }
  }, [exibirLogs]);

  useEffect(() => {
    if (userData?.email !== isEmailRodrigo) {
      setTimeout(() => navigate("/"), 3000);
    }
  }, [userData?.email, isEmailRodrigo, navigate]);

  useEffect(() => {
    const getAllUsers = async () => {
      try {
        const response = await api.get("/auth/all-users");
        const users = response.data.data || response.data || [];
        const activeUsers = users.filter((user) => user.status === "ativo" && user.statusAprovacao === "Aprovado");
        setAllUsers(activeUsers);
      } catch (error) {
        exibirLogs && console.log("Erro ao buscar todos os usuários", error);
        setAllUsers([]);
      }
    };
    getAllUsers();
  }, [exibirLogs]);

  useEffect(() => {
    let filtrados = [];
    if (tipoContaSelecionado) {
      filtrados = allUsers.filter((user) => user.tipoConta === tipoContaSelecionado);
    } else {
      filtrados = allUsers;
    }

    filtrados.sort((a, b) => {
      const specialEmail = "softcash@gmail.com";
      if (a.email === specialEmail) return -1;
      if (b.email === specialEmail) return 1;
      
      const nomeA = a.apelido || a.nome || '';
      const nomeB = b.apelido || b.nome || '';
      return nomeA.localeCompare(nomeB);
    });

    setUsuariosFiltrados(filtrados);
    setSelectedUserId("");
  }, [tipoContaSelecionado, allUsers]);

  useEffect(() => {
    const fetchReceipts = async () => {
      if (!selectedUserId) {
        setApiResponse(null);
        return;
      }
      setLoading(true);
      const selectedUser = allUsers.find((user) => user._id === selectedUserId);
      let termoBuscaFinal = "";
      if (selectedUser) {
        const termos = [selectedUser.codeUtm];
        if (selectedUser.utms && Array.isArray(selectedUser.utms)) {
          const subTermos = selectedUser.utms.flatMap((u) => [u.utm, u.perfil]);
          termos.push(...subTermos);
        }
        termoBuscaFinal = [...new Set(termos.filter(Boolean))].join(",");
      }
      if (!termoBuscaFinal) {
        setLoading(false);
        setApiResponse(null);
        return;
      }
      const params = {
        filtro: "comecaCom",
        termoBusca: termoBuscaFinal,
        dataInicio: format(selectedStartDate, "dd-MM-yyyy"),
        dataFim: format(selectedEndDate, "dd-MM-yyyy"),
        page: currentPage,
        limit: itemsPerPage,
      };
      try {
        const response = await api.get("/auth/receitas-users", { params });
        setApiResponse(response.data);
      } catch (error) {
        exibirLogs && console.error("Erro ao buscar receitas dos usuários: ", error);
        setApiResponse(null);
      } finally {
        setLoading(false);
      }
    };
    fetchReceipts();
  }, [selectedStartDate, selectedEndDate, selectedUserId, allUsers, exibirLogs, currentPage, itemsPerPage]);

  useEffect(() => {
    const fetchTotalConverted = async () => {
      const convertedValue = await getDolarCotation(totalReceitaTable || 0);
      setTotalConverted(convertedValue);
    };
    if (totalReceitaTable >= 0) {
      fetchTotalConverted();
    }
  }, [totalReceitaTable, getDolarCotation]);

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleUserChange = (e) => {
    setSelectedUserId(e.target.value);
    setCurrentPage(1);
  };

  const renderPaginationItems = () => {
    if (totalPages <= 6) {
      return [...Array(totalPages).keys()].map((pageNumber) => (
        <PaginationItem active={pageNumber + 1 === currentPage} key={pageNumber + 1}>
          <PaginationLink onClick={() => handlePageClick(pageNumber + 1)}>
            {pageNumber + 1}
          </PaginationLink>
        </PaginationItem>
      ));
    }

    const pages = [];
    const pageNumbersToShow = new Set();

    pageNumbersToShow.add(1);
    pageNumbersToShow.add(totalPages);
    pageNumbersToShow.add(currentPage);
    if (currentPage > 1) pageNumbersToShow.add(currentPage - 1);
    if (currentPage < totalPages) pageNumbersToShow.add(currentPage + 1);

    const sortedPages = Array.from(pageNumbersToShow).sort((a, b) => a - b);
    let lastPage = 0;

    sortedPages.forEach((page) => {
      // Adiciona reticências se houver um salto entre os números
      if (lastPage !== 0 && page - lastPage > 1) {
        pages.push(
          <PaginationItem key={`ellipsis-${lastPage}`} disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        );
      }
      
      pages.push(
        <PaginationItem active={page === currentPage} key={page}>
          <PaginationLink onClick={() => handlePageClick(page)}>
            {page}
          </PaginationLink>
        </PaginationItem>
      );
      
      lastPage = page;
    });

    return pages;
  };


  return (
    <div className="contentUserReceipt">
      {loading && <FullScreenLoader />}
      <Header urlFoto="https://i.imgur.com/nCIToeZ.png" />
      <div className="wrapper">
        <h1 className="titleReceitaUser">Receitas dos usuários</h1>
        <br />
        <div style={{ marginBottom: 20 }}>
          <Row>
            <Col xs={12} md={4} lg={4}>
              <FormGroup>
                <Label style={{ color: "#fff" }}>Período</Label>
                <button
                  className="dateInput"
                  onClick={() => setMostrarCalendario(true)}
                  style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                >
                  <FaRegCalendarAlt size={18} />
                  {selectedStartDate && selectedEndDate ? `${format(selectedStartDate, "dd/MM/yyyy")} - ${format(selectedEndDate, "dd/MM/yyyy")}` : "Selecione um período"}
                </button>
              </FormGroup>
            </Col>
            <Col xs={12} md={3} lg={3}>
              <FormGroup>
                <Label htmlFor="selectTipoConta" style={{ color: "#fff" }}>
                  Tipo de Conta
                </Label>
                <Input
                  id="selectTipoConta"
                  name="select"
                  type="select"
                  value={tipoContaSelecionado}
                  onChange={(e) => setTipoContaSelecionado(e.target.value)}
                >
                  <option value="">Selecione</option>
                  {tiposDeContaUnicos.map((tipo) => (
                    <option key={tipo} value={tipo}>
                      {tipo === "usuario" ? "Cortador" : tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col xs={12} md={5} lg={5}>
              <FormGroup>
                <Label htmlFor="selectUsers" style={{ color: "#fff" }}>
                  Usuários
                </Label>
                <Input
                  id="selectUsers"
                  name="select"
                  type="select"
                  value={selectedUserId}
                  onChange={handleUserChange}
                  disabled={!tipoContaSelecionado}
                >
                  <option value="">Selecione</option>
                  {usuariosFiltrados.map((user) => (
                    <option value={user._id} key={user._id}>
                      {user.apelido ? `${user.apelido} - ` : ""} {user.nome}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>
        </div>

        {mostrarCalendario && (
          <div className="overlayCalendar">
            <>
              <DateRange
                editableDateInputs={true}
                locale={ptBR}
                onChange={(item) => {
                  setPeriodTable([item.selection]);
                  setSelectedStartDate(item.selection.startDate);
                  setSelectedEndDate(item.selection.endDate);
                }}
                moveRangeOnFirstSelection={false}
                ranges={periodTable}
              />
              <button className="btnFecharCalendario" onClick={() => setMostrarCalendario(false)}>
                Confirmar
              </button>
            </>
          </div>
        )}

        <div style={{ marginBottom: 15, padding: 8, backgroundColor: "#fff", borderRadius: 10, display: "flex", justifyContent: "center", alignItems: "center", gap: 2, flexDirection: "column" }}>
          <strong style={{ fontSize: 27, fontWeight: "bold", color: "#00A500" }}>
            $ {formatarMoedaDolar(totalReceitaTable)}{" "}
            <span style={{ fontSize: 14, color: "black" }}>R$ {totalConverted}</span>
          </strong>
          <span style={{ fontSize: 15, color: "#888" }}>Total de receita</span>
        </div>

        <Table striped className="table-dark" responsive style={{ tableLayout: "auto" }}>
          <thead>
            <tr>
              <th>Link</th>
              <th>Impressões</th>
              <th>Receita</th>
              <th>RPM</th>
            </tr>
          </thead>
          <tbody>
            {tableData.length > 0 ? (
              tableData
                .sort((a, b) => b.impressoes - a.impressoes)
                .map((data, index) => {
                  const rpm = data.impressoes > 0 ? (data.receita / data.impressoes) * 1000 : 0;
                  return (
                    <tr key={index}>
                      <td><span>{data.utmCampaign}</span></td>
                      <td>{data.impressoes}</td>
                      <td>${data.receita ? data.receita.toFixed(2) : "0.00"}</td>
                      <td>${rpm > 0 ? rpm.toFixed(2) : "0.00"}</td>
                    </tr>
                  );
                })
            ) : (
              <tr>
                <td colSpan="5" style={{ textAlign: "center", padding: "20px" }}>
                  {loading ? 'Carregando...' : 'Selecione um usuário para ver os dados.'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        {totalPages > 1 && (
          <div className="d-flex justify-content-center pagination-container" >
            <Pagination aria-label="Navegação de páginas">
              <PaginationItem disabled={currentPage <= 1}>
                <PaginationLink previous onClick={() => handlePageClick(currentPage - 1)} />
              </PaginationItem>
              
              {renderPaginationItems()}

              <PaginationItem disabled={currentPage >= totalPages}>
                <PaginationLink next onClick={() => handlePageClick(currentPage + 1)} />
              </PaginationItem>
            </Pagination>
          </div>
        )}

        <div className="logoPage">
          <img src="https://i.imgur.com/nCIToeZ.png" alt="Logo do Link do bem" />
        </div>
        <div style={{ marginTop: 30 }}>
          <center>
            <span style={{ color: "#777" }}>v:{__APP_VERSION__}</span>
          </center>
        </div>
      </div>
    </div>
  );
}

export default ReceitasUsuarios;